// Drawn triangles + draw calls per object FAMILY, for the 5 s telemetry
// (PROTOCOL.md `telemetry` tris_by / calls_by; the step before an fps plan).
//
// Tagging: main.js hands each module a famScene(scene, fam) instead of the
// scene. It is the real scene behind a Proxy whose add() stamps
// `userData.fam` on the objects it adds (a module's group, a chunk mesh);
// everything else forwards to the scene, so the graph, draw order, culling
// and layers are untouched. A drawn object's family is its nearest tagged
// ancestor; none = `other`.
//
// Counting: no extra pass, no scene traversal. One frame in SAMPLE_EVERY
// (sample()) swaps renderer.renderBufferDirect — the call three makes once
// per draw — for a wrapper that books the renderer.info.render deltas
// (triangles = instances × tris, exactly three's own count) to the object's
// family; the frame after it draws with the original again. Every pass of
// the sampled frame counts (the optic + full view, both XR eyes). The window
// reports the MEAN per sampled frame, so the families sum to ≈ `tris` of
// one frame of the same passes.
//
// Pure (no three import): scripts/check-serve.mjs reads DRAW_FAMS.

// the closed family list — gs.js cleanTelemetry DRAW_FAMS takes no other key
export const DRAW_FAMS = ['ground', 'ring', 'buildings', 'structures', 'trees', 'shrubs',
  'grass', 'water', 'lifts', 'other'];
const SAMPLE_EVERY = 30;      // ~10 samples in a 5 s window at 60 fps

// the scene with an add() that tags (the module's own scene.add calls stay)
export function famScene(scene, fam) {
  const bound = new Map();
  const add = (...objs) => {
    for (const o of objs) if (o && o.userData && !o.userData.fam) o.userData.fam = fam;
    scene.add(...objs);
    return proxy;
  };
  const proxy = new Proxy(scene, {
    get(t, k) {
      if (k === 'add') return add;
      const v = t[k];
      if (typeof v !== 'function' || k === 'constructor') return v;
      let f = bound.get(k);
      if (!f) bound.set(k, f = v.bind(t));
      return f;
    },
  });
  return proxy;
}

export class FamTris {
  constructor(renderer) {
    this.r = renderer;
    this.info = renderer.info.render;
    this.n = 0;               // frames since the last sample
    this.frames = 0;          // sampled frames this window
    this.tris = new Float64Array(DRAW_FAMS.length);
    this.calls = new Float64Array(DRAW_FAMS.length);
    this.cache = new WeakMap();   // object -> family index, reset per window
    this.orig = renderer.renderBufferDirect;
    const self = this;
    this.wrap = function (camera, scene, geometry, material, object, group) {
      const i0 = self.info.triangles, c0 = self.info.calls;
      self.orig.call(this, camera, scene, geometry, material, object, group);
      const f = self.famOf(object);
      self.tris[f] += self.info.triangles - i0;
      self.calls[f] += self.info.calls - c0;
    };
  }

  famOf(obj) {
    let f = this.cache.get(obj);
    if (f !== undefined) return f;
    let o = obj;
    while (o && !(o.userData && o.userData.fam)) o = o.parent;
    f = o ? DRAW_FAMS.indexOf(o.userData.fam) : -1;
    if (f < 0) f = DRAW_FAMS.length - 1;     // other
    this.cache.set(obj, f);
    return f;
  }

  // draw() = the frame's render call(s); counted one frame in SAMPLE_EVERY
  sample(draw) {
    if (++this.n < SAMPLE_EVERY) { draw(); return; }
    this.n = 0;
    this.frames++;
    this.r.renderBufferDirect = this.wrap;
    try { draw(); } finally { this.r.renderBufferDirect = this.orig; }
  }

  // the window's {tris_by, calls_by} (mean per sampled frame, families > 0
  // only; null before a sample), then a fresh window
  window() {
    const k = this.frames;
    let out = null;
    if (k) {
      const tb = {}, cb = {};
      DRAW_FAMS.forEach((name, i) => {
        if (this.tris[i] > 0) tb[name] = Math.round(this.tris[i] / k);
        if (this.calls[i] > 0) cb[name] = Math.round(this.calls[i] / k);
      });
      out = { tris_by: tb, calls_by: cb };
    }
    this.frames = 0;
    this.tris.fill(0);
    this.calls.fill(0);
    this.cache = new WeakMap();
    return out;
  }
}
